import {ActionBar} from "@client/components/ActionBar";
import {AsyncButton} from "@client/components/Button";
import {
    BarSurface,
    BarSurfaceProvider,
    HeaderSurface,
    HeaderSurfaceProvider,
} from "@client/components/Glass";
import type {IconType} from "@client/components/Icon";
import {SimpleIcon} from "@client/components/Icon";
import {MenuIcon} from "@client/components/Navigation";
import {useActions} from "@client/components/Scene";
import {Segmented} from "@client/components/Segmented";
import {Toasts} from "@client/components/Toasts";
import {onSceneTransition} from "@client/gotterdammerung";
import type {
    RouterRenderPropsArgs,
    TransitionFn,
    TransitionClass,
    TransitionType,
} from "@client/gotterdammerung";
import * as hardware from "@client/hardware";
import {ScrollContainerContext} from "@client/journeys/contexts";
import type {PillAction} from "@client/router";
import {useAppDispatch, useAppSelector} from "@client/store";
import {MoonIcon, SunIcon} from "@heroicons/react/24/outline";
import {classNames} from "@reactivated";
import {
    AnimatePresence,
    MotionValue,
    animate,
    motion,
    useMotionValue,
    useScroll,
    useTransform,
} from "motion/react";
import React from "react";

function clamp(value: number, min: number, max: number) {
    return Math.min(Math.max(value, min), max);
}

function useBoundedScroll(
    threshold: number,
    container: React.RefObject<HTMLElement | null>,
) {
    const {scrollY} = useScroll({container});
    const scrollYBounded = useMotionValue(0);
    const scrollYBoundedProgress = useTransform(scrollYBounded, [0, threshold], [0, 1]);

    React.useEffect(() => {
        const unsubscribe = scrollY.on("change", (current) => {
            // iOS rubber-banding takes scrollTop negative on a pull and back
            // to zero on release; read raw, that return leg looks like a
            // scroll down and the header stays shrunk at the top. Clamp both
            // ends at zero, and at the top the header is simply open.
            const clamped = Math.max(current, 0);
            const previous = Math.max(scrollY.getPrevious() ?? 0, 0);
            const diff = clamped - previous;
            scrollYBounded.set(
                clamped === 0 ? 0 : clamp(scrollYBounded.get() + diff, 0, threshold),
            );
        });
        return () => unsubscribe();
    }, [threshold, scrollY, scrollYBounded]);

    return {scrollY, scrollYBoundedProgress};
}

interface ScrollContainerRenderProps {
    scrollY: MotionValue<number>;
    scrollYBoundedProgress: MotionValue<number>;
    // 1 when nothing is left to scroll (no overflow, or at the end), 0 while
    // content still runs under the bottom bar; fades over the last stretch.
    settled: MotionValue<number>;
    hasOverflow: boolean;
    isRefreshing: boolean;
}

export function ScrollContainer(props: {
    className?: string;
    onRefresh?: () => Promise<unknown>;
    // The tab on screen. Each keeps its own offset: a short tab arriving with a
    // long one's offset mounts scrolled past its end, and a view transition
    // captures the sticky header out of view, then it snaps in at the end.
    page?: string;
    children: (renderProps: ScrollContainerRenderProps) => React.ReactNode;
}) {
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const offsets = React.useRef(new Map<string | undefined, number>());
    const page = React.useRef(props.page);
    // Read on scroll: by the layout effect the new tab's content has replaced
    // the old one and clamped the scroller.
    const lastOffset = React.useRef(0);
    // In the commit, before a view transition takes its new picture.
    React.useLayoutEffect(() => {
        const element = scrollRef.current;
        if (element == null || page.current === props.page) {
            return;
        }
        offsets.current.set(page.current, lastOffset.current);
        page.current = props.page;
        element.scrollTop = offsets.current.get(props.page) ?? 0;
    }, [props.page]);
    const [hasOverflow, setHasOverflow] = React.useState(false);
    const platform = useAppSelector((state) => state.boot.platform);
    const [isRefreshing, setIsRefreshing] = React.useState(false);
    const startYRef = React.useRef<number | null>(null);
    const startedAtTopRef = React.useRef(false);
    const hasFiredRefreshRef = React.useRef(false);

    // Pull to refresh, iOS only: the webview has no native one. Not memoized
    // on purpose: these read props.onRefresh, and a memoized handler kept the
    // tab it was built on (#1810). Eligibility is decided on touchstart
    // because rubber-banding pushes scrollTop negative during the move.
    const handleTouchStart = (e: React.TouchEvent) => {
        if (platform !== "ios" || props.onRefresh == null) return;
        // A dialog opened from this scene is portaled out of the DOM but
        // still bubbles through the React tree; its touches are not ours.
        if (scrollRef.current?.contains(e.target as Node) !== true) return;
        startYRef.current = e.touches[0].pageY;
        startedAtTopRef.current = scrollRef.current.scrollTop <= 1;
        hasFiredRefreshRef.current = false;
    };
    const handleTouchMove = (e: React.TouchEvent) => {
        const {onRefresh} = props;
        if (platform !== "ios" || onRefresh == null) return;
        if (
            startYRef.current == null ||
            startedAtTopRef.current === false ||
            hasFiredRefreshRef.current === true
        ) {
            return;
        }
        if (e.touches[0].pageY - startYRef.current > 10) {
            hasFiredRefreshRef.current = true;
            setIsRefreshing(true);
            void Promise.resolve(onRefresh()).finally(() => setIsRefreshing(false));
        }
    };
    const handleTouchEnd = () => {
        startYRef.current = null;
        startedAtTopRef.current = false;
    };
    const {scrollY, scrollYBoundedProgress} = useBoundedScroll(200, scrollRef);
    const scrollRemaining = useMotionValue(0);
    const settled = useTransform(scrollRemaining, [0, 48], [1, 0], {clamp: true});
    const measureRemaining = React.useCallback(() => {
        const element = scrollRef.current;
        if (element == null) return;
        scrollRemaining.set(
            Math.max(
                element.scrollHeight - element.clientHeight - element.scrollTop - 1,
                0,
            ),
        );
    }, [scrollRemaining]);
    React.useEffect(
        () => scrollY.on("change", measureRemaining),
        [scrollY, measureRemaining],
    );
    const scrollContext = React.useMemo(
        () => ({
            scrollY,
            scrollYBoundedProgress,
            scrollTo: (top: number, duration = 0.15) => {
                if (scrollRef.current == null) return;
                const from = scrollRef.current.scrollTop;
                animate(from, top, {
                    duration,
                    ease: "easeOut",
                    onUpdate: (value) => scrollRef.current?.scrollTo(0, value),
                });
            },
        }),
        [scrollY, scrollYBoundedProgress],
    );

    React.useEffect(() => {
        const element = scrollRef.current;
        if (element == null) return;

        const checkOverflow = () => {
            // The always-scrollable pseudo element adds exactly 1px of
            // overflow; only report overflow beyond it.
            setHasOverflow(element.scrollHeight - element.clientHeight > 1);
            measureRemaining();
        };

        checkOverflow();
        const observer = new ResizeObserver(checkOverflow);
        observer.observe(element);
        // Content grows without the container resizing; watch its children.
        const inner = new MutationObserver(checkOverflow);
        inner.observe(element, {childList: true, subtree: true});

        return () => {
            observer.disconnect();
            inner.disconnect();
        };
    }, [measureRemaining]);

    return (
        <ScrollContainerContext.Provider value={scrollContext}>
            <div
                ref={scrollRef}
                className={classNames(
                    // Vertical-only: overflow-auto on both axes lets iOS grab
                    // the x-axis on diagonal pans even without real overflow.
                    "scrollbar-hidden flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden",
                    // The absolutely positioned 1px past the bottom edge keeps
                    // the container scrollable on iOS even when content fits
                    // (an in-flow spacer would be absorbed by the flex-1
                    // view), so the rubber band stays local instead of
                    // chaining to the webview body.
                    "relative overscroll-y-contain after:absolute after:top-full after:h-px after:w-px",
                    props.className,
                )}
                onScroll={(event) => {
                    lastOffset.current = event.currentTarget.scrollTop;
                }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchEnd}
            >
                {props.children({
                    scrollY,
                    scrollYBoundedProgress,
                    settled,
                    hasOverflow,
                    isRefreshing,
                })}
            </div>
        </ScrollContainerContext.Provider>
    );
}

// Tab type for SimpleTabs
export type SimpleTab<T extends string = string> = {
    id: T;
    label: string;
    Icon: React.ComponentType<{className?: string}>;
    isHidden?: boolean;
    hasErrors?: boolean;
};

// Tab layout constants (in pixels)
// TAB_WIDTH: w-20 = 5rem = 80px
// TAB_GAP: gap-1 = 0.25rem = 4px
// TAB_PADDING: p-2 = 0.5rem = 8px
const TAB_WIDTH = 80;
const TAB_GAP = 4;
const TAB_PADDING = 8;
const TAB_TOTAL_WIDTH = TAB_WIDTH + TAB_GAP;

// Using const type parameter (TS 5.0+) to infer literal tab IDs from the array
type TabId<T extends readonly SimpleTab[]> = T[number]["id"];

interface SimpleTabsProps<T extends readonly SimpleTab[]> {
    tabs: T;
    selectedTab: TabId<T>;
    onSelectTab: (tab: TabId<T>) => void;
}

// Shared tab content renderer - receives pre-filtered visible tabs
function TabContent({
    visibleTabs,
    selectedTab,
    onSelectTab,
    selectedIndex,
}: {
    visibleTabs: SimpleTab[];
    selectedTab: string;
    onSelectTab: (tab: string) => void;
    selectedIndex: number;
}) {
    return (
        <div className="inline-flex items-center gap-1 rounded-3xl border border-gray-100/80 bg-white/50 p-2 backdrop-blur-md dark:border-slate-700/80 dark:bg-slate-800/50">
            <motion.div
                className="absolute inset-y-2 w-20 rounded-2xl bg-gray-200 dark:bg-slate-600"
                initial={false}
                animate={{left: TAB_PADDING + selectedIndex * TAB_TOTAL_WIDTH}}
                transition={{type: "spring", stiffness: 500, damping: 35}}
            />
            {visibleTabs.map((tab) => {
                const {id, label, Icon} = tab;
                const isSelected = selectedTab === id;
                const colorClass = isSelected
                    ? "text-gray-900 dark:text-slate-100"
                    : tab.hasErrors === true
                      ? "text-red-500 dark:text-red-400"
                      : "text-gray-600 dark:text-slate-400";

                return (
                    <button
                        key={id}
                        type="button"
                        onClick={() => {
                            void hardware.haptic();
                            onSelectTab(id);
                        }}
                        className="relative z-10 flex w-20 flex-col items-center gap-1 py-2"
                    >
                        <Icon className={classNames("size-5", colorClass)} />
                        <span className={classNames("text-xs font-medium", colorClass)}>
                            {label}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

export function SimpleTabs<const T extends readonly SimpleTab[]>({
    tabs,
    selectedTab,
    onSelectTab,
}: SimpleTabsProps<T>) {
    const visibleTabs = tabs.filter((tab) => tab.isHidden !== true);
    const selectedIndex = visibleTabs.findIndex((tab) => tab.id === selectedTab);

    // Don't render if only 1 or no visible tabs (nothing to switch between)
    const showTabs = visibleTabs.length > 1;

    return (
        <AnimatePresence initial={false} mode="popLayout">
            {showTabs && (
                <motion.div
                    key="tabs"
                    layout
                    className="flex justify-center pt-2"
                    initial={{scale: 0.9, y: 10}}
                    animate={{
                        scale: 1,
                        y: 0,
                        transition: {type: "spring", stiffness: 400, damping: 30},
                    }}
                    exit={{scale: 0.9, y: 10, transition: {duration: 0.2}}}
                >
                    <TabContent
                        visibleTabs={visibleTabs}
                        selectedTab={selectedTab}
                        onSelectTab={onSelectTab}
                        selectedIndex={selectedIndex}
                    />
                </motion.div>
            )}
        </AnimatePresence>
    );
}

const DARK_QUERY = "(prefers-color-scheme: dark)";

// The styling is CSS: the root's data-dark drives the dark: variant (see
// input.css). The toggle only has to know what it is flipping away from.
function useDarkMode(enabled: boolean) {
    const [override, setOverride] = React.useState<"light" | "dark" | null>(null);
    const toggle = React.useCallback(() => {
        const current =
            override ?? (window.matchMedia(DARK_QUERY).matches ? "dark" : "light");
        setOverride(current === "dark" ? "light" : "dark");
    }, [override]);

    return {toggle, mode: enabled ? (override ?? "system") : undefined};
}

function DarkModeToggle(props: {toggle: () => void}) {
    return (
        <button
            type="button"
            onClick={props.toggle}
            className="p-1 text-gray-400 transition-colors hover:text-gray-600 dark:text-slate-400 dark:hover:text-slate-200"
            aria-label="Toggle dark mode"
        >
            <SunIcon className="hidden size-5 dark:block" />
            <MoonIcon className="size-5 dark:hidden" />
        </button>
    );
}

// A shrink curve on bounded rewinding scroll with a dead zone. The header no
// longer shrinks (its controls stay full size, as iOS keeps them); this stays
// for a scene that wants to scale something with the scroll.
export const useTitleScale = (scrollYBoundedProgress: MotionValue<number>) => {
    const delayedProgress = useTransform(
        scrollYBoundedProgress,
        [0, 0.5, 1],
        [0, 0, 1],
    );
    return useTransform(delayedProgress, [0, 1], [1, 0.8]);
};

function RefreshSpinner() {
    return (
        <motion.div
            className="size-6 rounded-full border-2 border-gray-300 border-t-gray-700"
            animate={{rotate: 360}}
            transition={{duration: 0.8, repeat: Infinity, ease: "linear"}}
        />
    );
}

// The primary bottom action: the bright pill, or a round when the bar also
// carries tabs and the row must stay one line.
export function Pill(props: {action: PillAction; compact?: boolean}) {
    const {action} = props;
    return (
        <BarSurface tone="primary" className="flex-none rounded-full">
            <AsyncButton
                aria-label={action.title}
                title={action.title}
                onClick={async () => {
                    void hardware.haptic();
                    await Promise.resolve(action.onClick());
                }}
                disabled={action.disabled}
                className={classNames(
                    "flex h-10 items-center justify-center gap-1.5 text-sm font-medium text-chrome-ink-strong disabled:opacity-50",
                    props.compact === true ? "w-10" : "px-5",
                )}
            >
                <SimpleIcon icon={action.icon} className="size-4" />
                {props.compact !== true && action.title}
            </AsyncButton>
        </BarSurface>
    );
}

export function Tile(props: {action: PillAction}) {
    const {action} = props;
    return (
        <BarSurface className="flex-none rounded-full">
            <AsyncButton
                aria-label={action.title}
                title={action.title}
                onClick={async () => {
                    void hardware.haptic();
                    await Promise.resolve(action.onClick());
                }}
                disabled={action.disabled}
                className="flex size-10 items-center justify-center disabled:opacity-50"
            >
                <span
                    className={classNames(
                        "flex",
                        action.tone === "danger" ? "text-red-600" : "text-chrome-ink",
                    )}
                >
                    <SimpleIcon icon={action.icon} className="size-4" />
                </span>
            </AsyncButton>
        </BarSurface>
    );
}

// Route tabs in the bar: the selected option is white on the glass pill.
function BarTabs(props: {
    tabs: readonly SimpleTab[];
    selected: string | undefined;
    onSelect: (id: string) => void;
}) {
    const layoutId = React.useId();
    return (
        <BarSurface className="rounded-full">
            <div className="flex p-1">
                {props.tabs
                    .filter((tab) => tab.isHidden !== true)
                    .map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => {
                                void hardware.haptic();
                                props.onSelect(tab.id);
                            }}
                            className="relative px-3 py-2 text-[12.5px]"
                        >
                            {tab.id === props.selected && (
                                <motion.span
                                    layoutId={layoutId}
                                    className="absolute inset-0 bg-white"
                                    style={{borderRadius: 9999}}
                                    transition={{
                                        type: "spring",
                                        stiffness: 500,
                                        damping: 35,
                                    }}
                                />
                            )}
                            <span
                                className={classNames(
                                    "relative z-10 block leading-none",
                                    tab.id === props.selected
                                        ? "font-medium text-chrome-ink-strong"
                                        : "font-normal text-chrome-ink",
                                    tab.hasErrors === true && "text-red-600",
                                )}
                            >
                                {tab.label}
                            </span>
                        </button>
                    ))}
            </div>
        </BarSurface>
    );
}

// A navigation's type picks the scene's classes; the keyframes are in input.css.
// Only transform, opacity and clip-path animate on the scene, so its frames stay
// on the compositor.
const SCENE: Record<TransitionType | "default", TransitionClass | "none"> = {
    forward: "joy-slide-forward",
    back: "joy-slide-back",
    start: "joy-fade",
    complete: "joy-fade",
    default: "none",
};

const HEADER: Record<TransitionType | "default", TransitionClass | "none"> = {
    forward: "joy-header",
    back: "joy-header",
    start: "joy-header",
    complete: "joy-header",
    default: "none",
};

// The header stays put through a slide and cross-fades what changes in it.
function HeaderTransition(props: {isModal: boolean; children: React.ReactElement}) {
    if (props.isModal) {
        return props.children;
    }
    return (
        <React.ViewTransition
            name="joy-header"
            share={HEADER}
            update={HEADER}
            enter="none"
            exit="none"
        >
            {props.children}
        </React.ViewTransition>
    );
}

function SimpleSceneContent({
    scrollY,
    settled,
    isRefreshing,
    isResolving,
    back,
    options,
    view,
    tabs,
    preparedActions,
    isModal,
    transition,
    darkModeToggle,
    activeTab,
}: {
    activeTab: string;
    scrollY: MotionValue<number>;
    settled: MotionValue<number>;
    isRefreshing: boolean;
    isResolving: boolean;
    back: {title: string; onClick: () => void} | null;
    options: RouterRenderPropsArgs["options"];
    view: React.ReactNode;
    tabs: RouterRenderPropsArgs["tabs"];
    preparedActions: ReturnType<typeof useActions>;
    isModal: boolean;
    transition: TransitionFn;
    darkModeToggle: React.ReactNode;
}) {
    const isMenuOpen = useAppSelector((state) => state.scenes.isMenuOpen);
    // The barcode scanner paints the camera under the webview; the shell's
    // chrome has to get out of the picture, like the legacy shell's nav does.
    const isScanning = useAppSelector((state) => state.search.isScanning);
    const headerRef = React.useRef<HTMLDivElement>(null);
    const [headerHeight, setHeaderHeight] = React.useState(0);
    // Measured before paint, so the content never starts under the header.
    React.useLayoutEffect(() => {
        const header = headerRef.current;
        if (header == null || isModal) {
            return;
        }
        setHeaderHeight(header.offsetHeight);
        const observer = new ResizeObserver(() => setHeaderHeight(header.offsetHeight));
        observer.observe(header);
        return () => observer.disconnect();
    }, [isModal]);
    const dispatch = useAppDispatch();
    const toggleMenu = () => dispatch({type: "TOGGLE_MENU"});
    const scene = options.scene?.kind === "SIMPLE" ? options.scene : undefined;
    const corner =
        scene?.corner != null ? (
            <Segmented
                size="compact"
                label={scene.corner.label}
                options={scene.corner.options}
                value={scene.corner.value}
                onChange={scene.corner.onChange}
            />
        ) : null;
    const rightSlot =
        darkModeToggle ??
        (isRefreshing || isResolving ? <RefreshSpinner /> : (corner ?? options.more));

    const simpleTabs = tabs
        .filter(
            (tab): tab is typeof tab & {icon: Exclude<typeof tab.icon, false>} =>
                tab.icon !== false,
        )
        .map((tab) => ({
            id: tab.title,
            label: tab.title,
            Icon:
                typeof tab.icon === "string"
                    ? (props: {className?: string}) => (
                          <SimpleIcon
                              icon={tab.icon as IconType}
                              className={props.className}
                          />
                      )
                    : tab.icon,
            isHidden: "isHidden" in tab ? tab.isHidden : undefined,
            hasErrors: tab.hasErrors,
        }));
    const hasTabs = simpleTabs.length > 1;
    const selectTab = (tabId: string) => {
        const tab = tabs.find((t) => t.title === tabId);
        if (tab == null) return;
        if (typeof tab.locator === "function") {
            tab.locator();
        } else {
            void transition(tab.locator);
        }
    };
    const tabsPill = (
        <SimpleTabs
            tabs={simpleTabs}
            selectedTab={tabs.find((tab) => tab.isActive)?.title ?? tabs[0]?.title}
            onSelectTab={selectTab}
        />
    );

    const hasBottomSection = options.bottomSection != null;
    const hasToolbar = options.toolbarYouAreInChargeOfTransparency != null;
    const hasActions = preparedActions.filter((a) => a != null).length > 0;
    const pills = scene?.bottom?.kind === "pills" ? scene.bottom : null;
    const hasPills =
        pills != null && (pills.primary != null || (pills.tiles?.length ?? 0) > 0);
    // Pills ride the same re-entrancy guard as actions: a double tap during
    // an in-flight save is dropped.
    const pillDefinitions: PillAction[] = [
        ...(pills?.primary != null ? [pills.primary] : []),
        ...(pills?.tiles ?? []),
    ];
    const guarded = useActions(pillDefinitions);
    const preparedPills = pillDefinitions.map((pill, index) => ({
        ...pill,
        onClick: guarded[index]?.onClick ?? pill.onClick,
    }));
    const preparedPrimary = pills?.primary != null ? preparedPills[0] : null;
    const preparedTiles =
        pills?.primary != null ? preparedPills.slice(1) : preparedPills;
    const tabsInBar = hasTabs;
    const hasBottomBar =
        hasBottomSection || hasToolbar || tabsInBar || hasActions || hasPills;

    // Tabs share the row with actions or with pills; everything else is
    // mutually exclusive.
    const exclusiveCount = [hasBottomSection, hasToolbar, hasActions, hasPills].filter(
        Boolean,
    ).length;
    if (exclusiveCount > 1) {
        throw new Error(
            "SimpleScene: bottomSection, toolbarYouAreInChargeOfTransparency, actions and pills cannot share the bottom bar",
        );
    }

    // One column shared by the header content, the body and the bottom bar.
    // The bands behind them always run the full window width, so a scene
    // background is never cut off at the column's edge.
    const widthClass = scene?.width === "column" ? "max-w-md" : "max-w-3xl";
    const surface = scene?.surface ?? {kind: "fade"};
    const hasMenu = (scene?.nav ?? "menu") !== "none";

    // The header rests at the top and is over content once the page scrolls,
    // on the same 48 px the bar uses at the other end.
    const headerRest = useTransform(scrollY, [0, 48], [1, 0], {clamp: true});
    const edgeOpacity = useTransform(headerRest, [0, 1], [1, 0]);
    // Dark keeps the opaque band: opaque within a short page's worth of
    // travel, so a screen that overflows by a few points still ends up with
    // content under a solid band.
    const backgroundOpacity = useTransform(scrollY, [0, 24], [0, 1], {clamp: true});
    const blurAmount = useTransform(scrollY, [0, 50], [0, 1.5], {clamp: true});
    const backdropFilter = useTransform(blurAmount, (blur) => `blur(${blur}px)`);
    const edgeColor =
        surface.kind === "custom" ? "var(--surface)" : "var(--color-stone-50)";

    const surfaceVars =
        surface.kind === "custom"
            ? ({
                  "--surface": surface.background,
                  "--surface-dark": surface.darkBackground ?? surface.background,
              } as React.CSSProperties)
            : undefined;
    // The scroll edge: no band behind the bubbles, a blur that deepens and a
    // fade to the scene's color, running past the header's bottom so content
    // dissolves into it. Outside a modal it is its own fixed layer under the
    // header, not part of it: in the header it overflowed the header's box, and
    // a view transition scales a snapshot's overflow into the box, so the
    // header shifted when its picture took over and again when it handed back.
    const scrollEdge = (
        <motion.div
            aria-hidden
            className={classNames(
                "pointer-events-none inset-x-0 top-0 dark:hidden",
                isModal ? "absolute -bottom-8" : "fixed z-20",
                isScanning && "invisible",
            )}
            style={{
                opacity: edgeOpacity,
                height: isModal ? undefined : headerHeight + 32,
                ...surfaceVars,
            }}
        >
            <div className="absolute inset-0 mask-b-from-60% backdrop-blur-[2px]" />
            <div className="absolute inset-0 mask-b-from-40% mask-b-to-75% backdrop-blur-[8px]" />
            <div
                className="absolute inset-0"
                style={{
                    background: `linear-gradient(color-mix(in oklch, ${edgeColor} 94%, transparent) 35%, color-mix(in oklch, ${edgeColor} 55%, transparent) 68%, transparent)`,
                }}
            />
        </motion.div>
    );

    return (
        <>
            {/* Blur and background are separate siblings to work around a Safari
                compositing bug: Safari won't repaint a child's dark: class change
                if a parent has backdropFilter (compositor layer swallows the
                invalidation). Keeping them as siblings avoids this. */}
            {isModal == false && scrollEdge}
            <HeaderTransition isModal={isModal}>
                <div
                    ref={headerRef}
                    data-scene-header={isModal ? undefined : ""}
                    // A top-level scene's header is fixed, not sticky: WebKit
                    // leaves a sticky header out of a view transition's picture
                    // whenever the scroller under it is scrolled, and it snaps
                    // in at the end. The spacer below holds its place.
                    className={classNames(
                        isModal ? "sticky" : "fixed inset-x-0",
                        "top-0 z-20",
                        isScanning && "invisible",
                    )}
                    style={surfaceVars}
                >
                    {isModal && scrollEdge}
                    <motion.div
                        className="absolute inset-0 hidden dark:block"
                        style={{backdropFilter}}
                    />
                    <motion.div
                        className={classNames(
                            "absolute inset-0 hidden dark:block",
                            surface.kind === "custom"
                                ? "bg-(--surface-dark)"
                                : "bg-slate-950",
                        )}
                        style={{opacity: backgroundOpacity}}
                    />
                    {/* One column, one step: the title row, an optional topSection
                    and the content below are spaced by this gap and this
                    padding, never by the pieces themselves. */}
                    <HeaderSurfaceProvider value={headerRest}>
                        <div
                            className={classNames(
                                "navigation-safe relative mx-auto flex w-full flex-col gap-joy-step px-4 pb-joy-step text-center",
                                widthClass,
                            )}
                        >
                            <div className="relative flex items-center justify-center">
                                {hasMenu && isModal == false && back == null && (
                                    <div className="absolute inset-y-0 left-0 flex items-center">
                                        <HeaderSurface className="rounded-full">
                                            <MenuIcon
                                                bare
                                                isMenuOpen={isMenuOpen}
                                                toggleMenu={toggleMenu}
                                            />
                                        </HeaderSurface>
                                    </div>
                                )}
                                {back != null && (
                                    <div className="absolute inset-y-0 left-0 flex items-center">
                                        <HeaderSurface className="rounded-full">
                                            <button
                                                type="button"
                                                aria-label={`Back to ${back.title}`}
                                                className="flex size-10 items-center justify-center text-chrome-ink dark:text-slate-300"
                                                onClick={() => {
                                                    void hardware.haptic();
                                                    back.onClick();
                                                }}
                                            >
                                                <SimpleIcon
                                                    icon="custom-chevron-left"
                                                    className="size-4"
                                                />
                                            </button>
                                        </HeaderSurface>
                                    </div>
                                )}
                                {/* Clearance for the side controls is SYMMETRIC and
                            lives here, in one place. It used to be split — a
                            pr-7 here plus a matching pl-7 that each titleSection
                            added for itself — which centres the box but not
                            what is drawn in it, and only as long as every
                            consumer remembers its half. Symmetric padding on
                            the centred element needs no counterpart: the middle
                            of the band stays the middle, and the title clears
                            the menu on the left and the toggle on the right. */}
                                <div
                                    className={classNames(
                                        "min-w-0 flex-1",
                                        rightSlot != null && "px-7",
                                    )}
                                >
                                    {options.titleSection != null ? (
                                        options.titleSection
                                    ) : (
                                        <div className="text-xl font-normal">
                                            {options.title}
                                        </div>
                                    )}
                                </div>
                                {/* Out of flow, like the menu and back controls on the
                            left: a side control does not get a say in where the
                            middle is. */}
                                {rightSlot != null && (
                                    <div className="absolute inset-y-0 right-0 flex items-center">
                                        {rightSlot}
                                    </div>
                                )}
                            </div>
                            {/* A top section that goes takes its share of the gap
                            with it, so the content below never jumps. No
                            initial={false}: it would switch off every initial
                            state inside the section (the search box's tutorial
                            ring showed for good). */}
                            <AnimatePresence>
                                {options.topSection != null && (
                                    <motion.div
                                        key="top-section"
                                        exit={{
                                            height: 0,
                                            marginTop:
                                                "calc(var(--spacing-joy-step) * -1)",
                                        }}
                                        transition={{duration: 0.2, ease: "easeOut"}}
                                    >
                                        {options.topSection}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </HeaderSurfaceProvider>
                </div>
            </HeaderTransition>
            {isModal == false && (
                <div aria-hidden className="flex-none" style={{height: headerHeight}} />
            )}
            <div
                className={classNames(
                    "relative mx-auto w-full flex-1 px-4",
                    widthClass,
                    isScanning && "invisible",
                )}
            >
                {view}
            </div>
            {/* The shell owns the bottom edge the way it owns the top one: the
                safe area is a painted strip under everything, so content
                stops above the home indicator at rest (10px floor in a
                browser) instead of running through it. Sections above it
                paint nothing (#1827); the step between content and a section
                and the section's column inset are the shell's, so a section
                brings no outer margins of its own. */}
            {/* One band per tab, never resized in place: tabs of one scene
                (the journal and its workout) would otherwise share this node,
                and WebKit paints the old tab's band at the new tab's height
                for a couple of frames when a slide snapshots the switch. */}
            <div
                key={activeTab}
                className={classNames(
                    "sticky bottom-0 z-20",
                    hasBottomBar && "pt-joy-step",
                    isScanning && "invisible",
                )}
            >
                <Toasts />
                {hasBottomSection && (
                    <BarSurfaceProvider value={settled}>
                        <div className={classNames("mx-auto w-full px-4", widthClass)}>
                            {options.bottomSection}
                        </div>
                    </BarSurfaceProvider>
                )}
                {hasToolbar && (
                    <div className="transform-gpu">
                        {options.toolbarYouAreInChargeOfTransparency}
                    </div>
                )}
                {hasPills && (
                    <BarSurfaceProvider value={settled}>
                        <div
                            className={classNames(
                                "mx-auto flex w-full items-center justify-center px-4",
                                tabsInBar ? "gap-1.5" : "gap-2",
                                widthClass,
                            )}
                        >
                            {preparedTiles.map((tile, index) => (
                                <Tile key={index} action={tile} />
                            ))}
                            {tabsInBar && (
                                <BarTabs
                                    tabs={simpleTabs}
                                    selected={
                                        tabs.find((tab) => tab.isActive)?.title ??
                                        tabs[0]?.title
                                    }
                                    onSelect={selectTab}
                                />
                            )}
                            {preparedPrimary != null && (
                                <Pill action={preparedPrimary} compact={tabsInBar} />
                            )}
                        </div>
                    </BarSurfaceProvider>
                )}
                {((tabsInBar && !hasPills) || hasActions) && (
                    <div
                        className={classNames(
                            "mx-auto flex w-full transform-gpu items-center gap-2 px-8",
                            widthClass,
                            tabsInBar && hasActions
                                ? "justify-between"
                                : "justify-center",
                        )}
                    >
                        {tabsInBar && tabsPill}
                        {hasActions && (
                            <ActionBar
                                actions={preparedActions}
                                actionsStyle="contrast"
                            />
                        )}
                    </div>
                )}
                {/* Spacing only. A custom surface's backdrop already reaches
                    the window edge (WalkingClub's gradient); painting the
                    header color here bands it. */}
                <div
                    className={classNames(
                        "pb-safe",
                        surface.kind === "fade" && "bg-stone-50 dark:bg-slate-950",
                    )}
                />
            </div>
        </>
    );
}

export function SimpleScene({
    view,
    options,
    parent,
    actions,
    tabs,
    close,
    transition,
    locator,
    isResolving,
    ...rest
}: Pick<
    RouterRenderPropsArgs,
    "view" | "options" | "parent" | "locator" | "tabs" | "actions"
> & {
    close: ((result: null) => void) | null;
    isLoading: boolean;
    isResolving: boolean;
    transition: TransitionFn;
}) {
    void rest;
    const backInfo =
        options.back != null && typeof options.back != "string"
            ? options.back
            : parent != null && options.back != null
              ? {locator: parent, title: options.back}
              : null;

    let back = null;

    if (backInfo != null) {
        if ("onClick" in backInfo) {
            back = {
                title: backInfo.title,
                onClick: backInfo.onClick,
            };
        } else {
            const backLocator = backInfo.locator;

            if ("linkFromAppToSite" in backLocator) {
                throw new Error("Unsupported locator for back");
            }
            back = {
                title: backInfo.title,
                onClick: () => {
                    void transition(backLocator);
                },
            };
        }
    } else if (typeof options.back == "string" && close != null) {
        back = {
            title: options.back,
            onClick: () => {
                close(null);
            },
        };
    }
    const isModal = close != null;

    const preparedActions = useActions(actions);
    const darkMode = useDarkMode(options.respectDarkMode === true);

    const scene = (
        <div
            data-scene
            data-dark={darkMode.mode}
            className={classNames(
                // A modal fills the dialog panel it is mounted in; only a
                // top-level scene owns the viewport.
                isModal ? "h-full" : "h-dvh",
                "flex w-full flex-col bg-stone-50 transition-colors duration-200",
                "dark:bg-slate-950",
            )}
        >
            <ScrollContainer onRefresh={options.onRefresh} page={locator.tab}>
                {({scrollY, settled, isRefreshing}) => (
                    <SimpleSceneContent
                        scrollY={scrollY}
                        settled={settled}
                        isRefreshing={isRefreshing}
                        isResolving={isResolving}
                        back={back}
                        options={options}
                        isModal={isModal}
                        view={view}
                        tabs={tabs}
                        preparedActions={preparedActions}
                        transition={transition}
                        activeTab={locator.tab}
                        darkModeToggle={
                            options.respectDarkMode === true ? (
                                <DarkModeToggle toggle={darkMode.toggle} />
                            ) : null
                        }
                    />
                )}
            </ScrollContainer>
        </div>
    );

    if (isModal) {
        return scene;
    }

    // One name for every top-level scene: a tab switch updates it and a new scene
    // shares it, so the old and new pictures slide as one pair.
    return (
        <>
            <title>{options.title}</title>
            <React.ViewTransition
                name="joy-scene"
                share={SCENE}
                update={SCENE}
                enter="none"
                exit="none"
                onShare={onSceneTransition}
                onUpdate={onSceneTransition}
            >
                {scene}
            </React.ViewTransition>
        </>
    );
}
